import React, { useEffect, useState } from 'react';
import { Line } from 'react-chartjs-2';
import { fetchTemperatureData, fetchDeployments } from '../../services/api';
import 'chart.js/auto';
import { TemperatureData, DeploymentDate, ChartData } from '../../types/types';
import { annotations, legendData } from '../../utils/chart';
import { findDeploymentByDate } from '../../utils/deployment';

interface TemperatureChartProps {
  selectedDate: string | null;
  setSelectedDeployment: (deploymentName: string) => void;
  setSelectedDate: (date: string) => void;
  isDark: boolean;
}

const fillMissingDates = (
  data: TemperatureData[],
  startDateStr: string,
  endDateStr: string
): TemperatureData[] => {
  const tempMap = new Map(data.map((d) => [d.date, d.avg_temperature]));
  const result: TemperatureData[] = [];
  let current = new Date(startDateStr);
  const end = new Date(endDateStr);

  while (current <= end) {
    const isoDate = current.toISOString().slice(0, 10);

    const isJune7 = current.getUTCMonth() === 5 && current.getUTCDate() === 7;
    const isJune26 = current.getUTCMonth() === 5 && current.getUTCDate() === 26;
    const isSeptember4 =
      current.getUTCMonth() === 8 && current.getUTCDate() === 4;
    const isSeptember5 =
      current.getUTCMonth() === 8 && current.getUTCDate() === 5;

    result.push({
      date: isoDate,
      avg_temperature: isJune7
        ? 13.6
        : isJune26
          ? 11.2
          : isSeptember4
            ? 12.7
            : isSeptember5
              ? 14.6
              : (tempMap.get(isoDate) ?? 0),
    });

    current.setDate(current.getDate() + 1);
  }

  return result;
};

const TemperatureChart: React.FC<TemperatureChartProps> = ({
  selectedDate,
  setSelectedDeployment,
  setSelectedDate,
  isDark,
}) => {
  const [chartData, setChartData] = useState<ChartData | null>(null);
  const [temperatureData, setTemperatureData] = useState<TemperatureData[]>([]);
  const [deploymentDates, setDeploymentDates] = useState<DeploymentDate[]>([]);

  useEffect(() => {
    fetchTemperatureData()
      .then((data: TemperatureData[]) => {
        const filledData = fillMissingDates(data, '2024-10-01', '2025-11-18');
        setTemperatureData(filledData);
      })
      .catch((err) => console.error('Error loading temperature data:', err));

    fetchDeployments()
      .then((data: DeploymentDate[]) => {
        setDeploymentDates(data);
      })
      .catch((err) => console.error('Error loading deployment data:', err));
  }, []);

  useEffect(() => {
    if (temperatureData.length === 0 || deploymentDates.length === 0) return;

    const dates = temperatureData.map((entry) => entry.date);
    const temperatures = temperatureData.map((entry) => entry.avg_temperature);

    const pointColors = dates.map((date) => {
      const isSelected = date === selectedDate;
      const isDeploymentDate = deploymentDates.some((d) => d.date === date);
      return isSelected ? 'red' : isDeploymentDate ? 'blue' : 'gray';
    });

    const pointSizes = dates.map((date) => {
      const isSelected = date === selectedDate;
      const isDeploymentDate = deploymentDates.some((d) => d.date === date);
      return isSelected ? 8 : isDeploymentDate ? 6 : 0;
    });

    const borderColor = isDark ? 'white' : 'black';
    const backgroundColor = isDark
      ? 'rgba(255, 255, 255, 0.3)'
      : 'rgba(0, 0, 0, 0.3)';

    setChartData({
      labels: dates,
      datasets: [
        {
          label: '🌡️ Avg Temperature (°C)',
          data: temperatures,
          borderColor: borderColor,
          backgroundColor: backgroundColor,
          tension: 0.3,
          fill: false,
          pointRadius: pointSizes,
          pointBackgroundColor: pointColors,
        },
      ],
    });
  }, [temperatureData, deploymentDates, selectedDate, isDark]);

  const handleClick = (event: any) => {
    const chart = event.chart;
    const elements = chart.getElementsAtEventForMode(
      event.native,
      'nearest',
      { intersect: true },
      false
    );

    if (elements.length > 0) {
      const elementIndex = elements[0].index;
      const clickedDate = chartData?.labels?.[elementIndex];
      const chosenDeployment = findDeploymentByDate(
        clickedDate || '',
        deploymentDates
      );
      if (chosenDeployment) {
        setSelectedDeployment(`Deployment_${chosenDeployment.number}`);
        setSelectedDate(chosenDeployment.date);
      }
    }
  };

  const filteredData = selectedDate
    ? temperatureData.filter((d) => d.date === selectedDate)
    : temperatureData;

  if (!chartData) return <p>Loading data...</p>;

  return (
    <div
      className={`dark:text-white ${isDark ? 'bg-neutral-900' : 'bg-white'}`}
    >
      <div
        className={`relative h-48 w-full ${isDark ? 'dark:bg-neutral-900' : 'bg-white'}`}
      >
        <Line
          data={chartData}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            onClick: handleClick,
            scales: {
              x: {
                ticks: {
                  maxTicksLimit: 12,
                  color: isDark ? 'white' : 'black',
                },
                grid: {
                  color: isDark
                    ? 'rgba(255, 255, 255, 0.3)'
                    : 'rgba(0, 0, 0, 0.1)',
                },
                type: 'time',
                time: {
                  unit: 'month',
                },
              },
              y: {
                ticks: {
                  callback: (value) => `${value} °C`,
                  color: isDark ? 'white' : 'black',
                },
                grid: {
                  color: isDark
                    ? 'rgba(255, 255, 255, 0.3)'
                    : 'rgba(0, 0, 0, 0.1)',
                },
              },
            },
            plugins: {
              legend: {
                display: true,
                onClick: () => {},
                labels: {
                  color: isDark ? 'white' : 'black',
                  generateLabels: function () {
                    return legendData.map((item) => ({
                      text: item.label,
                      fillStyle: item.backgroundColor,
                      fontColor: isDark ? 'white' : 'black',
                    }));
                  },
                },
              },
              annotation: {
                annotations,
              },
            },
          }}
        />
      </div>
      {selectedDate && filteredData.length > 0 && (
        <div
          className={`flex justify-center items-center p-4 mt-4 rounded-t-lg shadow-lg
            ${isDark ? 'dark:bg-neutral-900 dark:text-white' : 'bg-white text-black border-gray-300'}`}
        >
          <div className="text-center">
            <p className="font-semibold text-lg text-blue-500">
              Selected Deployment Date: {selectedDate}
            </p>
            <p className="text-md">
              🌡️ Avg Temperature: {filteredData[0].avg_temperature.toFixed(1)}{' '}
              °C
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default TemperatureChart;
